import {useEffect, useRef, useState} from "react";
import moment from "moment";
import {useNavigate} from "react-router-dom";
import {Checkbox, FormControlLabel} from "@mui/material";

import Page from "@/components/Page";
import {
	DsChips,
	DsEmpty,
	DsHeader,
	DsMissingSources,
	DsResultWarnings,
	DsSection,
	DsSkeleton,
	DsTimelineEvent,
	dayLabel,
} from "@/components/DsKit";

import {useClubSnapshot} from "@/utils/hooks";
import {
	CONTROL_DEPARTMENTS,
	buildTimeline,
	groupByDay,
} from "@/utils/clubControl";

const RANGES = [
	{value: 7, label: "± 7 giorni"},
	{value: 14, label: "± 14 giorni"},
	{value: 30, label: "± 30 giorni"},
];

function DsTimelinePage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {
		data: s,
		loading,
		reload,
		scope,
	} = useClubSnapshot({handleNotification});

	const [range, setRange] = useState(14);
	const [showExamples, setShowExamples] = useState(false);
	const [department, setDepartment] = useState("all");
	const todayRef = useRef(null);

	const now = moment();
	const events = buildTimeline(s, {includePreview: showExamples}).filter(
		(e) =>
			Math.abs(moment(e.date).diff(now, "days", true)) <= range &&
			(department === "all" || e.department === department),
	);
	const days = groupByDay(events);

	useEffect(() => {
		if (s && todayRef.current) {
			todayRef.current.scrollIntoView({behavior: "smooth", block: "start"});
		}
	}, [s?.loadedAt]); // eslint-disable-line react-hooks/exhaustive-deps

	const all = buildTimeline(s).filter(
		(e) => Math.abs(moment(e.date).diff(now, "days", true)) <= range,
	);
	const chipOptions = [
		{value: "all", label: "Tutto", count: all.length},
		...CONTROL_DEPARTMENTS.map((d) => ({
			value: d.id,
			label: d.label,
			count: all.filter((e) => e.department === d.id).length,
		})).filter((o) => o.count > 0),
	];

	return (
		<Page>
			<div className="ds">
				<DsHeader
					title="Linea temporale"
					subtitle="Cosa è successo e cosa succederà, in tutto il club"
					snapshot={s}
					scope={scope}
					onReload={reload}
					reloading={loading}
				/>
				<DsSkeleton visible={loading && !s} />
				{s && (
					<>
						<DsMissingSources sources={s.missingSources} />
						<DsResultWarnings teams={s.teams} />
						<div className="ds-toolbar">
							<DsChips
								options={chipOptions}
								value={department}
								onChange={setDepartment}
							/>
							<DsChips options={RANGES} value={range} onChange={setRange} />
							<FormControlLabel
								className="ds-muted"
								control={
									<Checkbox
										size="small"
										checked={showExamples}
										onChange={(e) => setShowExamples(e.target.checked)}
									/>
								}
								label="Mostra esempi dimostrativi (esclusi dai conteggi)"
							/>
						</div>

						<DsSection>
							<div className="ds-timeline">
								{days.map((day) => {
									const isToday = day.today;
									const past = isToday
										? day.items.filter((e) => moment(e.date).isBefore(now))
										: [];
									const future = isToday
										? day.items.filter((e) => !moment(e.date).isBefore(now))
										: day.items;
									return (
										<div
											key={day.key}
											ref={isToday ? todayRef : undefined}
											className={
												"ds-timeline__day" + (isToday ? " is-today" : "")
											}
										>
											<div className="ds-timeline__day-label">
												{dayLabel(day.key)}
											</div>
											{(isToday ? past : []).map((e) => (
												<DsTimelineEvent
													key={e.id}
													event={e}
													onOpen={e.path ? () => navigate(e.path) : undefined}
												/>
											))}
											{isToday && (
												<div className="ds-timeline__now">
													Adesso · {now.format("HH:mm")}
												</div>
											)}
											{future.map((e) => (
												<DsTimelineEvent
													key={e.id}
													event={e}
													onOpen={e.path ? () => navigate(e.path) : undefined}
												/>
											))}
											{isToday && !day.items.length && (
												<DsEmpty>Nessun avvenimento oggi.</DsEmpty>
											)}
										</div>
									);
								})}
							</div>
						</DsSection>
					</>
				)}
			</div>
		</Page>
	);
}

export default DsTimelinePage;
